import { ConnectedPosition } from "@angular/cdk/overlay";
import { Router } from "@angular/router";
import { BehaviorSubject, EMPTY, from, Observable, of, Subject } from "rxjs";
import { distinctUntilChanged, filter, switchMap, take, takeUntil } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { whenUiQuiet } from "./onboarding-lesson-modal";

/** What a Learn orientation lesson shows right now - null when it has nothing to point at. */
export interface LearnOrientationPrompt {
  /** The lesson id tracked with the prompt - the step key, or a variant of it (L01A / L01B). */
  lessonId: string;
  /** A `[flipCoachTarget]` key on the Learn surface. */
  target: string;
  heading: string;
  body: string;
  lockedSide?: "below" | "above";
  /** Placement from 768px up, used as given - mobile stays viewport-centred. */
  desktopPositions?: ConnectedPosition[];
}

/**
 * The shared flow of the Learn first-use lessons (FLIP-373: L01 landing, L02 chapter, L03 topic) -
 * informational anchored prompts without buttons (Figma "Anchored Prompt (no buttons)") on the
 * coach-mark engine (FLIP-303). Each lesson supplies its step key, milestone and prompt; the host
 * surface drives it: enter() on init, setReady() whenever its state changes, onTargetUsed() from
 * the real target control, leave() on destroy.
 *
 * - Eligible while the `onboarding-walkthrough` flag is on and the lesson's step row is neither
 *   completed nor dismissed. A failed read holds back. No backfill for existing users - the Learn
 *   lessons are "eligible by surface under the existing independent lesson rules".
 * - Shown once the host reports ready (content loaded, no lesson modal covering the page, for the
 *   landing and chapter pages) and the blocking-UI signal has been quiet for the standard period.
 * - The visible close is the standard informational dismissal and completes permanently, as does
 *   using the real target control while the prompt is visible. Completion writes CompletedAt,
 *   which *is* the lesson's milestone.
 * - The surface stopping being ready, or the user leaving it, closes the prompt and records
 *   nothing: it shows again next time.
 * - Independent: no lesson reads another's state, and none launches another.
 *
 * The Nova first-use lessons (FLIP-384: N01, N03A, N03B, N04) reuse this flow, with two opt-in
 * hooks whose defaults are the Learn behaviour: `eligible$()` for conditions beyond the step row
 * (Nova Companion enabled, H01 resolved) and `overDialogs` for a target inside the Nova chat dialog.
 */
export abstract class LearnOrientationLesson {
  /** The UserOnboardingStep key. */
  protected abstract readonly stepKey: string;
  /** Recorded with the completed lesson state - the lesson's CompletedAt *is* this milestone. */
  protected abstract readonly milestone: string;

  private readonly leave$ = new Subject<void>();
  private readonly ready$ = new BehaviorSubject<boolean>(false);

  /**
   * True once this lesson is no longer pending - completed (this session or on an earlier one) or
   * dismissed. A lesson that follows this one (N03A then N03B, FLIP-384) waits on it. Emitted after
   * the prompt has closed, so a follower's prompt is never torn down by this one's. Not set while
   * the state is unknown (a failed read, or the check has not finished).
   */
  readonly done$ = new BehaviorSubject<boolean>(false);

  private entered = false;
  /** The step row once this entry's check found it eligible (null: no row yet). */
  private eligibleStep: OnboardingStep | null | undefined;
  /** Completed this session (its row may not have been re-read yet). */
  private completed = false;
  /** StartedAt has been written this session. */
  private started = false;
  /** The prompt on screen, or null. */
  private shown: LearnOrientationPrompt | null = null;

  constructor(
    protected readonly router: Router,
    private readonly coachMarkService: CoachMarkService,
    private readonly blockingUiSignal: BlockingUiSignalService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly paymentsService: PaymentsService,
    private readonly onboardingStepService: OnboardingStepService,
    private readonly interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - drop the entry and this session's state so nothing carries over.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.leave();
      this.completed = false;
      this.started = false;
      this.done$.next(false);
    });
  }

  /** What to show now, or null when the surface has nothing for this lesson to point at. */
  protected abstract prompt(): LearnOrientationPrompt | null;

  /**
   * Extra eligibility beyond the flag and the step row - emits once. Default: always eligible. A
   * lesson that must wait for something (H01 resolving) emits when it is satisfied and never
   * emits if it can't be.
   */
  protected eligible$(): Observable<boolean> {
    return of(true);
  }

  /**
   * True for a prompt whose target is inside an open dialog (the Nova chat): shown as soon as the
   * host is ready instead of waiting for the blocking-UI signal to go quiet, and stacked above the
   * dialog.
   */
  protected get overDialogs(): boolean {
    return false;
  }

  /**
   * True for a lesson that follows another one "immediately" (L01C): shown as soon as the host is
   * ready, without waiting out the blocking-UI quiet period. Default false.
   */
  protected get immediate(): boolean {
    return false;
  }

  /** Whether the current route is this lesson's surface - a guard alongside the host's leave(). */
  protected abstract isOnSurface(): boolean;

  /** Whether the prompt is on screen. */
  get isShowing(): boolean {
    return this.shown !== null;
  }

  /** Whether the lesson is no longer pending - see `done$`. */
  get isDone(): boolean {
    return this.done$.value;
  }

  /** Whether the lesson completed this session. */
  get isCompleted(): boolean {
    return this.completed;
  }

  /** Call when the host surface opens. Starts this entry's eligibility check. */
  enter(): void {
    this.leave();
    this.entered = true;
    this.eligibleStep = undefined;
    this.done$.next(this.completed);

    this.paymentsService.subscriptionActive$
      .pipe(
        filter((active) => active),
        take(1),
        switchMap(() => from(this.featureFlagService.init())),
        filter(() => this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)),
        switchMap(() => this.eligible$().pipe(take(1))),
        filter((eligible) => eligible),
        switchMap(() => this.onboardingStepService.getStep(this.stepKey)),
        takeUntil(this.leave$)
      )
      .subscribe((step) => this.onChecked(step));
  }

  /**
   * Call whenever the host's state changes: true once its content has loaded and nothing (a
   * lesson modal) covers it. False closes the prompt without recording anything.
   */
  setReady(ready: boolean): void {
    if (!this.entered) {
      return;
    }
    if (!ready) {
      this.closePrompt();
    }
    this.ready$.next(ready);
  }

  /**
   * Call from the real target control. Completes the lesson when the prompt is visible - `action`
   * names the control for tracking; otherwise a no-op.
   */
  onTargetUsed(action: string): void {
    if (!this.shown || this.completed) {
      return;
    }
    this.track(this.shown.lessonId, "onboarding_lesson_action", `action: ${action}`);
    this.complete();
  }

  /** Call when leaving the host surface - closes the prompt and records nothing. */
  leave(): void {
    this.leave$.next();
    this.closePrompt();
    this.entered = false;
    this.ready$.next(false);
  }

  private onChecked(step: OnboardingStep | null | undefined): void {
    if (step === undefined) {
      return;
    }
    if (step?.completedAt || step?.dismissed || this.completed) {
      this.done$.next(true);
      return;
    }
    this.eligibleStep = step;
    this.started = this.started || !!step?.startedAt;

    // Re-armed each time the host becomes ready, so a prompt closed by a lesson modal opening over
    // the page comes back once it closes.
    this.ready$
      .pipe(
        distinctUntilChanged(),
        switchMap((ready) => {
          if (!ready) {
            return EMPTY;
          }
          return this.overDialogs || this.immediate ? of(true) : whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$);
        }),
        takeUntil(this.leave$)
      )
      .subscribe(() => this.maybeOpen());
  }

  private maybeOpen(): void {
    if (this.completed || this.shown || this.eligibleStep === undefined || !this.ready$.value || !this.isOnSurface()) {
      return;
    }
    const prompt = this.prompt();
    if (prompt) {
      this.open(prompt);
    }
  }

  private open(prompt: LearnOrientationPrompt): void {
    this.shown = prompt;
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: this.stepKey,
      lastViewedAt: now,
      ...(this.started ? {} : { startedAt: now }),
    });
    this.started = true;

    // The engine tracks started / presented and the close press for lessonId.
    this.coachMarkService.show({
      target: prompt.target,
      heading: prompt.heading,
      message: prompt.body,
      lessonId: prompt.lessonId,
      lockedSide: prompt.lockedSide,
      desktopPositions: prompt.desktopPositions,
      showOverDialogs: this.overDialogs || undefined,
      fallback: { heading: prompt.heading, message: prompt.body },
      onDismiss: () => {
        // Standard informational dismissal - the engine closes the prompt itself.
        this.shown = null;
        this.complete(prompt.lessonId);
      },
    });
  }

  private complete(lessonId = this.shown?.lessonId ?? this.stepKey): void {
    this.completed = true;
    this.closePrompt();
    this.onboardingStepService.upsertStep({ stepKey: this.stepKey, completedAt: new Date() });
    this.track(lessonId, "onboarding_lesson_state_changed", `completed; milestone: ${this.milestone}`);
    // Last, once the prompt is closed: a lesson chained after this one opens its own on this signal.
    this.done$.next(true);
  }

  private closePrompt(): void {
    if (this.shown) {
      this.shown = null;
      this.coachMarkService.dismiss();
    }
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(lessonId: string, eventName: string, detail: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: `lesson: ${lessonId}; ${detail}`,
    });
  }
}
